import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, DatePicker, Empty, Modal, Table, Tag, message } from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  ExecutionRecalcChange,
  executionNormApis,
} from "../../../api/ActivityExecution";

// Restating captured production — the only thing in this screen that moves a
// number someone has already reported.
//
// Saving a back-dated norm version deliberately leaves history alone: a day's
// production was derived, reported and possibly billed under the norms in force
// when it was captured. Sometimes the revision IS a correction of the past, and
// then the old figures are the wrong ones. That call belongs to a person, so
// this modal always PREVIEWS first and only commits on a second, explicit click.

interface Props {
  open: boolean;
  activityId: number;
  activityLabel: string;
  onClose: () => void;
  onApplied: () => void;
}

const RecalculateNormsModal: React.FC<Props> = ({
  open,
  activityId,
  activityLabel,
  onClose,
  onApplied,
}) => {
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState<any>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setRange(null);
      setPreview(null);
      setError("");
    }
  }, [open]);

  const runPreview = async () => {
    if (!range) return;
    setLoading(true);
    setError("");
    try {
      const res = await executionNormApis.recalculate({
        activityId,
        fromDate: range[0].format("YYYY-MM-DD"),
        toDate: range[1].format("YYYY-MM-DD"),
        preview: true,
      });
      if (res?.status !== true) {
        setError(res?.message || "Could not work out what would change");
        return;
      }
      setPreview(res.data || null);
    } catch (err: any) {
      setError(err?.message || "Could not work out what would change");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!range) return;
    setApplying(true);
    try {
      const res = await executionNormApis.recalculate({
        activityId,
        fromDate: range[0].format("YYYY-MM-DD"),
        toDate: range[1].format("YYYY-MM-DD"),
        preview: false,
      });
      if (res?.status !== true) {
        message.error(res?.message || "Could not restate those days");
        return;
      }
      message.success(res.data?.message || "Days restated");
      onApplied();
      onClose();
    } catch (err: any) {
      message.error(err?.message || "Could not restate those days");
    } finally {
      setApplying(false);
    }
  };

  // A restatement's net effect on each BOQ line, which is the number a QS
  // actually has to defend — a per-day list of 500 rows is not that.
  const byTask = useMemo(() => {
    const totals = new Map<
      number,
      { taskLabel: string; delta: number; days: Set<string> }
    >();
    ((preview?.changes || []) as ExecutionRecalcChange[]).forEach((change) => {
      const row =
        totals.get(change.taskId) ||
        { taskLabel: change.taskLabel, delta: 0, days: new Set<string>() };
      row.delta += Number(change.delta) || 0;
      row.days.add(change.executionDate);
      totals.set(change.taskId, row);
    });
    return Array.from(totals.entries())
      .map(([taskId, row]) => ({
        taskId,
        taskLabel: row.taskLabel,
        delta: row.delta,
        dayCount: row.days.size,
      }))
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  }, [preview]);

  return (
    <Modal
      open={open}
      title="Restate captured days"
      onCancel={onClose}
      width={760}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="preview"
          onClick={runPreview}
          loading={loading}
          disabled={!range}
        >
          {preview ? "Recheck" : "Check what would change"}
        </Button>,
        <Button
          key="apply"
          type="primary"
          danger
          loading={applying}
          // Nothing to apply until a preview has actually found something. This
          // is the guard that keeps "restate" from being a one-click accident.
          disabled={!preview || !preview.entryCount}
          onClick={apply}
        >
          Restate {preview?.entryCount || 0} day
          {preview?.entryCount === 1 ? "" : "s"}
        </Button>,
      ]}
    >
      <div style={{ color: "#8094ae", fontSize: 12, marginBottom: 12 }}>
        Days captured before a norm revision keep the norms they were derived
        with — that is deliberate, so a month already reported cannot move on its
        own. Use this when the revision was a <strong>correction of the past</strong>{" "}
        and those figures are the ones that are wrong.
        <br />
        Each day is re-derived against the version in force on <em>its own</em>{" "}
        date, so a range spanning two versions restates each half correctly.
      </div>

      <div style={{ marginBottom: 12 }}>
        <span style={{ marginRight: 8, fontWeight: 600 }}>{activityLabel}</span>
        <DatePicker.RangePicker
          value={range as any}
          onChange={(value) => {
            setRange(value as any);
            setPreview(null);
          }}
          format="DD-MMM-YYYY"
          allowClear={false}
        />
      </div>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}

      {preview && !preview.entryCount && (
        <Alert
          type="success"
          showIcon
          message="Nothing to restate"
          description="Every captured day in this range already matches the norms in force for its date."
        />
      )}

      {!!preview?.entryCount && (
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={preview.message}
            description={
              preview.truncated
                ? "The detail below is capped at 500 rows; the totals are exact."
                : undefined
            }
          />
          <Table
            size="small"
            rowKey="taskId"
            pagination={{ pageSize: 8, showSizeChanger: false }}
            dataSource={byTask}
            columns={[
              { title: "BOQ line", dataIndex: "taskLabel" },
              {
                title: "Days affected",
                dataIndex: "dayCount",
                width: 120,
                align: "right" as const,
              },
              {
                title: "Net change",
                dataIndex: "delta",
                width: 160,
                align: "right" as const,
                render: (value: number) => (
                  // Signed and coloured: the direction of a restatement is the
                  // first thing anyone approving it looks for.
                  <Tag color={value > 0 ? "green" : value < 0 ? "red" : "default"}>
                    {value > 0 ? "+" : ""}
                    {value.toLocaleString(undefined, { maximumFractionDigits: 4 })}
                  </Tag>
                ),
              },
            ]}
          />
        </>
      )}

      {!!preview?.skipped?.length && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message={`${preview.skipped.length} day(s) could not be restated`}
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {preview.skipped.slice(0, 5).map((row: any) => (
                <li key={row.entryId}>
                  {dayjs(row.executionDate).format("DD-MMM-YYYY")} — {row.reason}
                </li>
              ))}
            </ul>
          }
        />
      )}

      {!preview && !error && !loading && !range && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Pick a date range to check"
        />
      )}
    </Modal>
  );
};

export default RecalculateNormsModal;
